import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Tabs, 
  Tab, 
  IconButton, 
  Typography,
  Paper,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import colors from '../../../styles/colors';

/**
 * ResultTabs - 結果分頁組件
 * 
 * 用於顯示多個工具結果，每個結果顯示在獨立的分頁中
 * 結果不會存入資料庫，僅在當前工作階段保留
 * 
 * @param {Array} results - 結果陣列，每個元素包含 { id, title, content, tool, timestamp }
 * @param {number} activeTab - 當前選中的分頁索引
 * @param {Function} onTabChange - 切換分頁的回調
 * @param {Function} onCloseTab - 關閉分頁的回調
 */
export const ResultTabs = ({ 
  results = [], 
  activeTab = 0,
  onTabChange,
  onCloseTab
}) => {
  const [copySuccess, setCopySuccess] = useState(null);
  const contentRef = useRef(null);

  // 恢復 localStorage 中保存的表單值
  useEffect(() => {
    if (results.length === 0 || activeTab >= results.length) return;
    
    const currentResult = results[activeTab];
    if (!currentResult || !currentResult.content) return;

    // 延遲執行以確保 DOM 已渲染
    const timer = setTimeout(() => {
      const container = document.getElementById(`result-tabpanel-${activeTab}`);
      if (!container) return;

      // 查找所有帶有 data-storage-key 屬性的容器
      const storageContainer = container.querySelector('[data-storage-key]');
      if (!storageContainer) return;

      const storageKey = storageContainer.getAttribute('data-storage-key');
      if (!storageKey) return;

      // 恢復所有 textarea 的值
      const textareas = storageContainer.querySelectorAll('textarea[id^="' + storageKey + '"]');
      textareas.forEach(textarea => {
        const textareaId = textarea.id;
        const savedValue = localStorage.getItem(textareaId);
        if (savedValue && !textarea.dataset.loaded) {
          textarea.value = savedValue;
          textarea.dataset.loaded = '1';
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, results]);

  const handleCopyContent = async (content) => {
    try {
      // 移除 HTML 標籤，只複製純文字
      const textContent = content.replace(/<[^>]*>/g, '');
      await navigator.clipboard.writeText(textContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('複製失敗:', err);
    }
  };

  if (results.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: 3,
          color: '#666',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: '#999' }}>
          尚無結果
        </Typography>
        <Typography variant="caption" sx={{ color: '#bbb', mt: 1 }}>
          選擇節點並使用工具來產生結果
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* 分頁標籤 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => onTabChange?.(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 32,
            '& .MuiTab-root': {
              minHeight: 32,
              padding: '4px 8px',
              fontSize: '0.75rem',
            },
          }}
          aria-label="結果分頁"
        >
          {results.map((result, index) => (
            <Tab
              key={result.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{result.title || `結果 ${index + 1}`}</span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseTab?.(result.id);
                    }}
                    sx={{ 
                      padding: '2px',
                      marginLeft: '4px',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
                    }}
                    aria-label={`關閉 ${result.title || `結果 ${index + 1}`}`}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              }
              id={`result-tab-${index}`}
              aria-controls={`result-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* 分頁內容 */}
      {results.map((result, index) => (
        <Box
          key={result.id}
          role="tabpanel"
          hidden={activeTab !== index}
          id={`result-tabpanel-${index}`}
          aria-labelledby={`result-tab-${index}`}
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: activeTab === index ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          {activeTab === index && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* 結果標題和元資訊 */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                px: 1,
                py: 0.5,
                borderBottom: '1px solid #eee',
                backgroundColor: '#fafafa',
                flexShrink: 0,
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    工具：{result.tool} | {new Date(result.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
                <Tooltip title={copySuccess ? '已複製！' : '複製內容'}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyContent(result.content)}
                    aria-label="複製結果內容"
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* 結果內容 */}
              <Box
                sx={{
                  flex: 1,
                  overflow: result.content?.includes('<iframe') ? 'hidden' : 'auto',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {result.loading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: 150,
                    color: colors.primary.main
                  }}>
                    <Typography variant="body2">處理中...</Typography>
                  </Box>
                ) : (
                  <Box
                    component="div"
                    sx={{ 
                      flex: 1,
                      overflow: result.content?.includes('<iframe') ? 'hidden' : 'auto',
                      lineHeight: 1.5,
                      fontSize: '0.875rem',
                      padding: result.content?.includes('<iframe') ? 0 : '8px 12px',
                      height: '100%',
                      '& p': { margin: '0 0 6px 0' },
                      '& ul, & ol': { paddingLeft: 2, margin: '6px 0' },
                      '& li': { marginBottom: '4px' },
                      '& h4': { margin: '8px 0 6px 0', fontSize: '0.95rem' },
                      '& iframe': { 
                        width: '100%', 
                        height: '100%',
                        minHeight: '100%',
                        border: 'none',
                      },
                      '& > div': {
                        height: result.content?.includes('<iframe') ? '100%' : 'auto',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: result.content }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

ResultTabs.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    content: PropTypes.string,
    tool: PropTypes.string,
    timestamp: PropTypes.number,
    loading: PropTypes.bool,
  })),
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  onCloseTab: PropTypes.func,
};

export default ResultTabs;
